'use strict';

import axios from 'axios';
import { listenDOMEvent } from '@jspartials/dom-api-utils';
import { isPointingDevice } from '@jspartials/general-utils';
import {
  classes,
  addClassToElement,
  removeClassToElement
} from '@jspartials/class-list-utils';
import Recommendations from '@jsclasses/recommendations';
import {
  initQuantityButtons,
  initRemoveProductLineButtons
} from '../cart/productLineActions';

// Selectors
const selector = {
  miniCart: '.js-mini-cart',
  icon: '.js-mini-cart-icon',
  wrapper: {
    miniCart: '.js-mini-cart__wrapper',
    miniCartList: '.js-mini-cart__list',
    miniCartResume: '.js-mini-cart__resume',
    miniCartEmpty: '.js-mini-cart__empty',
    recommendations: '.js-mini-cart__recommendations',
    grandTotalGross: '.js-mini-cart__grand-total-gross',
    grandTotalWithoutShipping: '.js-mini-cart__grand-total-without-shipping',
    shippingDiscount: '.js-mini-cart__shipping-discount',
    shippingDiscountValue: '.js-mini-cart__shipping-discount-value',
    orderDiscount: '.js-mini-cart__order-discount',
    orderDiscountValue: '.js-mini-cart__order-discount-value'
  },
  totalQuantity: {
    all: '.js-mini-cart__total-quantity',
    icon: '.js-mini-cart__total-quantity--icon',
    text: '.js-mini-cart__total-quantity--text'
  }
};

// Elements
const element = {
  miniCart: null,
  icon: null,
  wrapper: {
    miniCart: null,
    miniCartList: null,
    miniCartResume: null,
    miniCartEmpty: null,
    recommendations: null,
    grandTotalGross: null,
    grandTotalWithoutShipping: null,
    shippingDiscount: null,
    shippingDiscountValue: null,
    orderDiscount: null,
    orderDiscountValue: null
  },
  totalQuantity: {
    all: null,
    icon: null,
    text: null
  }
};

// Variables
let miniCartIsLoaded = false;

const listenMiniCartIcon = () => {
  if (!globalVariables.currentScreenIsCart && isPointingDevice()) {
    element.icon = document.querySelector(selector.icon) || null;
    element.miniCart = document.querySelector(selector.miniCart) || null;

    if (element.icon) {
      listenDOMEvent(element.icon, 'mouseenter', openMiniCart);
      listenDOMEvent(element.icon, 'mouseleave', animateOutMiniCart);
    }
  }
};

const openMiniCart = async () => {
  animateInMiniCart();
  if (!miniCartIsLoaded) {
    setLoadingMiniCart();

    await axios
      .post(globalVariables.endpoint.getMiniCart)
      .then((response) => {
        const { data = {} } = response;
        const { html = '' } = data;

        paintMiniCart(html);
      })
      .catch((error) => {
        console.error(error);
        unsetLoadingMiniCart();
      });
  }
};

const animateInMiniCart = () => {
  if (element.miniCart) {
    addClassToElement(element.miniCart, classes.is.open);
  }
};

const animateOutMiniCart = () => {
  if (element.miniCart) {
    removeClassToElement(element.miniCart, classes.is.open);
  }
};

const setLoadingMiniCart = () => {
  if (element.miniCart) {
    addClassToElement(element.miniCart, classes.is.loading);
  }
};

const unsetLoadingMiniCart = () => {
  if (element.miniCart) {
    removeClassToElement(element.miniCart, classes.is.loading);
  }
};

const paintMiniCart = (html) => {
  if (element.miniCart) {
    miniCartIsLoaded = true;
    element.miniCart.innerHTML = html;
    initMiniCart();
  }
};

const initMiniCart = () => {
  initElements();
  initRecommendations();
  initQuantityButtons(element.wrapper.miniCart);
  initRemoveProductLineButtons(element.wrapper.miniCart);

  document.addEventListener('refreshCart', (e) => {
    updateMiniCart(e.detail.data);
  });
};

const initElements = () => {
  element.icon = document.querySelector(selector.icon) || null;
  element.wrapper.miniCart =
    document.querySelector(selector.wrapper.miniCart) || null;
  element.wrapper.miniCartList =
    document.querySelector(selector.wrapper.miniCartList) || null;
  element.wrapper.miniCartResume =
    document.querySelector(selector.wrapper.miniCartResume) || null;
  element.wrapper.miniCartEmpty =
    document.querySelector(selector.wrapper.miniCartEmpty) || null;
  element.wrapper.recommendations =
    document.querySelector(selector.wrapper.recommendations) || null;
  element.wrapper.grandTotalGross =
    document.querySelector(selector.wrapper.grandTotalGross) || null;
  element.wrapper.grandTotalWithoutShipping =
    document.querySelector(selector.wrapper.grandTotalWithoutShipping) || null;
  element.wrapper.shippingDiscount =
    document.querySelector(selector.wrapper.shippingDiscount) || null;
  element.wrapper.shippingDiscountValue =
    document.querySelector(selector.wrapper.shippingDiscountValue) || null;
  element.wrapper.orderDiscount =
    document.querySelector(selector.wrapper.orderDiscount) || null;
  element.wrapper.orderDiscountValue =
    document.querySelector(selector.wrapper.orderDiscountValue) || null;
};

const initRecommendations = () => {
  if (element.wrapper.recommendations) {
    new Recommendations({
      wrapper: element.wrapper.recommendations,
      showPrice: true,
      maxVisibleItems: 2
    });
  }
};

const getMiniCartTotalQuantity = () => {
  axios
    .post(globalVariables.endpoint.getTotalItemsInCart)
    .then((response) => {
      const { data = {} } = response;
      const { totalItems = 0 } = data;

      updateMiniCartTotalQuantity(totalItems);
    })
    .catch((error) => {
      console.error('Error getting total items in cart', error);
    });
};

const updateMiniCartTotalQuantity = (totalItems) => {
  const totalQuantityElements =
    document.querySelectorAll(selector.totalQuantity.all) || [];

  totalQuantityElements.forEach((totalQuantityElement) => {
    totalQuantityElement.innerHTML = totalItems;
  });
};

const updateMiniCart = (data) => {
  const { basketModel = {} } = data;
  const { numItems = 0 } = basketModel;

  if (numItems > 0) {
    updateListProductLineItems(data);
    showMiniCart();
  } else {
    hideMiniCart();
  }
};

const updateListProductLineItems = (data) => {
  const miniCartList =
    document.querySelector(selector.wrapper.miniCartList) || null;
  const { basketModel = {} } = data;
  const { listHtml = '' } = data;

  if (miniCartList) {
    miniCartList.innerHTML = listHtml;
    initQuantityButtons(element.wrapper.miniCart);
    initRemoveProductLineButtons(element.wrapper.miniCart);
    updateMiniCartResume(basketModel);
  }
};

const updateMiniCartResume = (data) => {
  const { totals = {} } = data;
  const { showGrossPrice = false } = totals;
  const { merchandizeTotalGrossPrice = '' } = totals;
  const { grandTotalWithoutShipping = '' } = totals;
  const { shippingDiscount = {} } = totals;
  const { lineItemText: shippingDiscountText = '' } = shippingDiscount;
  const { orderLevelDiscountTotal = {} } = totals;
  const { value: orderDiscountValue = 0 } = orderLevelDiscountTotal;
  const { formatted: orderDiscountFormatted = '' } = orderLevelDiscountTotal;

  // Update total gross price
  if (element.wrapper.grandTotalGross) {
    element.wrapper.grandTotalGross.innerHTML = merchandizeTotalGrossPrice;

    // Add or remove class to show gross price
    if (showGrossPrice) {
      removeClassToElement(element.wrapper.grandTotalGross, classes.hidden);
    } else {
      addClassToElement(element.wrapper.grandTotalGross, classes.hidden);
    }
  }

  // Update grand total price (without shipping)
  if (element.wrapper.grandTotalWithoutShipping) {
    element.wrapper.grandTotalWithoutShipping.innerHTML =
      grandTotalWithoutShipping;

    // Add or remove class to show gross price
    if (showGrossPrice) {
      addClassToElement(
        element.wrapper.grandTotalWithoutShipping,
        classes.has.gross
      );
    } else {
      removeClassToElement(
        element.wrapper.grandTotalWithoutShipping,
        classes.has.gross
      );
    }
  }

  // Update order discount
  if (element.wrapper.orderDiscountValue) {
    element.wrapper.orderDiscountValue.innerHTML = `- ${orderDiscountFormatted}`;
  }

  // Add or remove class to show order discount
  if (element.wrapper.orderDiscount) {
    if (orderDiscountValue === 0) {
      addClassToElement(element.wrapper.orderDiscount, classes.hidden);
    } else {
      removeClassToElement(element.wrapper.orderDiscount, classes.hidden);
    }
  }

  // Update shipping discount
  if (element.wrapper.shippingDiscountValue) {
    element.wrapper.shippingDiscountValue.innerHTML = shippingDiscountText;
  }

  // Add or remove class to show shipping discount
  if (element.wrapper.shippingDiscount) {
    if (Object.keys(shippingDiscount).length === 0) {
      addClassToElement(element.wrapper.shippingDiscount, classes.hidden);
    } else {
      removeClassToElement(element.wrapper.shippingDiscount, classes.hidden);
    }
  }
};

const showMiniCart = () => {
  if (element.wrapper.miniCartList) {
    removeClassToElement(element.wrapper.miniCartList, classes.hidden);
  }

  if (element.wrapper.miniCartResume) {
    removeClassToElement(element.wrapper.miniCartResume, classes.hidden);
  }

  if (element.wrapper.miniCartEmpty) {
    addClassToElement(element.wrapper.miniCartEmpty, classes.hidden);
  }
};

const hideMiniCart = () => {
  if (element.wrapper.miniCartList) {
    addClassToElement(element.wrapper.miniCartList, classes.hidden);
  }

  if (element.wrapper.miniCartResume) {
    addClassToElement(element.wrapper.miniCartResume, classes.hidden);
  }

  if (element.wrapper.miniCartEmpty) {
    removeClassToElement(element.wrapper.miniCartEmpty, classes.hidden);
  }
};

export {
  listenMiniCartIcon,
  getMiniCartTotalQuantity,
  updateMiniCartTotalQuantity
};
